/**
 * Movements "Stock flow" (21 #7): where stock came from and where it went in the chosen period — suppliers,
 * returns and count corrections → the five stores → sites, lab use and write-offs — drawn as bands sized by
 * pieces (or lines). It tells the plain truth: when nothing left the stores in the app, it says so, because
 * site deliveries are then happening on paper. A band or box filters the ledger below. Built from the
 * ledger lines the page already has (no extra request); phones get the same numbers as a list.
 */
import { useMemo, useState } from 'react';
import { Info, MousePointerClick, Route } from 'lucide-react';
import { Segmented } from '@/components/ui/segmented';
import { formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { InvCard, LinkButton, UpperLabel, useElementWidth } from './InventoryUi';
import { formatQty, locationColor, plural, stockFlow } from './inventoryModel';

const COMPACT_BELOW = 680;
const LEFT_W = 168;
const MID_W = 196;
const RIGHT_W = 196;
const MAX_BAND = 26;
const MIN_BAND = 4;
/** Room above the boxes for the column headings (SOURCES · STORES · OUT OF STORES). */
const HEAD = 22;

const activate = (handler) => ({
  role: 'button',
  tabIndex: 0,
  onClick: handler,
  onKeyDown: (event) => {
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handler(); }
  }
});

export default function StockFlowCard({ movements = [], periodLabel = 'All time', onFilter, activeLocation = null, activeType = 'all', canRecordSiteDn = false, onRecordSiteDn }) {
  const [measure, width] = useElementWidth();
  const [unit, setUnit] = useState('pcs');
  const flow = useMemo(() => stockFlow(movements), [movements]);
  const value = (entry) => (unit === 'pcs' ? entry.qty : entry.lines);
  const show = (number) => (unit === 'pcs' ? formatQty(number) : formatNumber(number));
  // Unmeasured (no layout engine) or narrow: the same numbers as a list.
  const compact = width < COMPACT_BELOW;

  const pick = {
    source: (entry) => onFilter?.({ type: entry.filter, location: null }),
    store: (location) => onFilter?.({ location: activeLocation === location ? null : location }),
    out: (entry) => onFilter?.({ type: entry.filter, location: null })
  };

  const note = flow.totalIn > 0 && flow.totalOut === 0 ? (
    <>
      Nothing has left the stores in the app{flow.firstIn ? ` since the first receipt on ${formatDate(flow.firstIn)}` : ''} — site deliveries may be happening on paper.
      {canRecordSiteDn ? <> <LinkButton className="ml-1 text-[12.5px]" onClick={() => onRecordSiteDn?.()} data-testid="stock-flow-record-dn">Record a Site DN</LinkButton></> : null}
    </>
  ) : flow.totalIn === 0 && flow.totalOut === 0 ? (
    <>No stock moved in {periodLabel.toLowerCase() === 'all time' ? 'the app yet' : `the ${periodLabel.toLowerCase()}`}.</>
  ) : (
    <>Last stock out {formatDate(flow.lastOut)} · {formatQty(flow.totalIn)} in, {formatQty(flow.totalOut)} out{flow.transfers.qty ? ` · ${formatQty(flow.transfers.qty)} moved between stores` : ''}.</>
  );

  return (
    <InvCard className="px-5 py-4" data-testid="stock-flow">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3 className="flex items-center gap-2 font-heading text-[15px] font-bold text-ink"><Route className="h-4 w-4 text-ink-3" aria-hidden="true" /> Stock flow</h3>
        <span className="text-[13px] text-ink-3" data-testid="stock-flow-summary">
          {periodLabel} · <b className="font-semibold text-ink-2">{formatQty(flow.totalIn)} pcs in</b> · <b className="font-semibold text-ink-2">{formatQty(flow.totalOut)} pcs out</b>
        </span>
        <span className="flex-1" />
        <Segmented size="sm" ariaLabel="Size the bands by" value={unit} onChange={setUnit} options={[{ key: 'pcs', label: 'Pcs' }, { key: 'lines', label: 'Lines' }]} testId="stock-flow-unit" />
      </div>

      <div ref={measure} className="mt-3 min-w-0">
        {compact ? (
          <FlowList flow={flow} value={value} show={show} pick={pick} activeLocation={activeLocation} activeType={activeType} />
        ) : (
          <FlowDiagram flow={flow} width={width} value={value} show={show} pick={pick} activeLocation={activeLocation} activeType={activeType} />
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line-2 pt-2.5 text-[12.5px] text-ink-3">
        <p className="flex min-w-0 flex-1 items-start gap-1.5" data-testid="stock-flow-note">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" /><span>{note}</span>
        </p>
        <span className="hidden items-center gap-1.5 sm:inline-flex"><MousePointerClick className="h-3.5 w-3.5" aria-hidden="true" /> Click a band or store to filter the ledger</span>
      </div>
    </InvCard>
  );
}

function FlowDiagram({ flow, width, value, show, pick, activeLocation, activeType }) {
  const midX = Math.round(width / 2 - MID_W / 2);
  const rightX = width - RIGHT_W;
  const leftX = 0;

  // Stores: busy ones are taller and navy; the empty ones are slim outlines.
  let y = HEAD;
  const stores = flow.stores.map((entry) => {
    const busy = entry.in > 0 || entry.out > 0;
    const box = { ...entry, busy, x: midX, y, w: MID_W, h: busy ? 34 : 22 };
    y += box.h + 6;
    return box;
  });
  const storesHeight = y - 6;
  const sources = flow.sources.map((entry, index) => ({ ...entry, x: leftX, w: LEFT_W, h: index === 0 ? 50 : 24 }));
  let sy = HEAD;
  sources.forEach((box) => { box.y = sy; sy += box.h + 10; });
  const outs = flow.outs.map((entry, index) => ({ ...entry, x: rightX, w: RIGHT_W, h: 30, y: HEAD + index * 42 + 8 }));
  const height = Math.max(storesHeight, sy - 10, HEAD + 8 + 2 * 42 + 30) + 2;

  const bands = flow.bands.filter((band) => value(band) > 0);
  const biggest = Math.max(1, ...bands.map(value));
  const thickness = (band) => Math.max(MIN_BAND, Math.min(MAX_BAND, (value(band) / biggest) * MAX_BAND));
  const boxOf = (key) => sources.find((box) => box.key === key) || outs.find((box) => box.key === key) || stores.find((box) => box.location === key);
  // Stack the bands that leave / enter the same box so they do not overlap.
  const used = new Map();
  const slot = (box, side, t) => {
    const key = `${box.key || box.location}:${side}`;
    const offset = used.get(key) || 0;
    used.set(key, offset + t);
    const total = bands.filter((band) => (side === 'out' ? band.from : band.to) === (box.key || box.location)).reduce((sum, band) => sum + thickness(band), 0);
    const top = box.y + Math.max(0, (box.h - total) / 2) + offset;
    return { top, bottom: top + t };
  };
  const paths = bands.map((band) => {
    const from = boxOf(band.from);
    const to = boxOf(band.to);
    if (!from || !to) return null;
    const t = thickness(band);
    const a = slot(from, 'out', t);
    const b = slot(to, 'in', t);
    const x1 = from.x + from.w;
    const x2 = to.x;
    const mx = (x1 + x2) / 2;
    const outgoing = Boolean(flow.outs.find((entry) => entry.key === band.to));
    return {
      band,
      outgoing,
      d: `M${x1},${a.top} C${mx},${a.top} ${mx},${b.top} ${x2},${b.top} L${x2},${b.bottom} C${mx},${b.bottom} ${mx},${a.bottom} ${x1},${a.bottom} Z`,
      labelX: mx,
      labelY: (a.top + a.bottom + b.top + b.bottom) / 4
    };
  }).filter(Boolean);

  // Dashed "0" connectors from the busiest store to every way out nothing went through.
  const mainStore = [...stores].filter((box) => box.busy).sort((left, right) => right.in - left.in)[0];
  const zeroOuts = mainStore ? outs.filter((box) => !bands.some((band) => band.to === box.key)) : [];

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block max-w-full overflow-visible" role="group" aria-label="Stock flow from sources through the stores to where it went" data-testid="stock-flow-diagram">
      {[['SOURCES', leftX], ['STORES', midX], ['OUT OF STORES', rightX]].map(([label, x]) => (
        <text key={label} x={x + 2} y={11} className="fill-[color:var(--ink-3)] text-[10.5px] font-bold" style={{ letterSpacing: '0.07em' }} aria-hidden="true">{label}</text>
      ))}
      {paths.map(({ band, outgoing, d, labelX, labelY }) => (
        <g key={band.key} {...activate(() => (outgoing ? pick.out(flow.outs.find((entry) => entry.key === band.to)) : pick.source(flow.sources.find((entry) => entry.key === band.from))))} aria-label={`${show(value(band))} ${band.from} to ${band.to}`} className="cursor-pointer outline-none [&:focus-visible>path]:stroke-[color:var(--brand)] [&:hover>path]:opacity-80" data-testid={`stock-flow-band-${band.from}-${band.to}`.replace(/\s+/g, '-')}>
          <path d={d} fill={outgoing ? 'var(--status-violet-bg)' : 'var(--status-green-bg)'} stroke="transparent" strokeWidth="1.5" />
          <text x={labelX} y={labelY + 4} textAnchor="middle" className="pointer-events-none fill-[color:var(--ink-2)] text-[12px] font-semibold">{show(value(band))}</text>
        </g>
      ))}
      {zeroOuts.map((box) => {
        const x1 = mainStore.x + mainStore.w;
        const y1 = mainStore.y + mainStore.h / 2;
        const x2 = box.x - 6;
        const y2 = box.y + box.h / 2;
        const mx = (x1 + x2) / 2;
        return (
          <g key={`zero-${box.key}`} aria-hidden="true">
            <path d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2 - 18},${y2}`} fill="none" stroke="var(--line-dashed, var(--line))" strokeWidth="1.2" strokeDasharray="3 3" />
            <circle cx={x2 - 9} cy={y2} r="8" fill="var(--surface)" stroke="var(--line)" />
            <text x={x2 - 9} y={y2 + 3.5} textAnchor="middle" className="fill-[color:var(--ink-3)] text-[10px] font-semibold">0</text>
          </g>
        );
      })}
      {sources.map((box, index) => {
        const live = box.qty > 0;
        const on = activeType === box.filter && box.key !== 'found';
        return (
          <g key={box.key} {...activate(() => pick.source(box))} aria-label={`${box.label}: ${show(value(box))}`} aria-pressed={on} className="cursor-pointer outline-none [&:focus-visible>rect]:stroke-[color:var(--brand)]" data-testid={`stock-flow-source-${box.key}`}>
            {on ? <ActiveRing box={box} rx={12} /> : null}
            <rect x={box.x + 0.5} y={box.y + 0.5} width={box.w - 1} height={box.h - 1} rx="10" fill={live ? 'var(--status-green-bg)' : 'var(--surface)'} stroke={on ? 'var(--brand)' : live ? 'var(--status-green)' : 'var(--line)'} strokeWidth={on ? 2 : 1} />
            {index === 0 ? (
              <>
                <text x={box.x + 12} y={box.y + 20} className="fill-[color:var(--ink)] text-[12.5px] font-bold">{box.label}</text>
                <text x={box.x + 12} y={box.y + 40} className="fill-[color:var(--ink)] font-heading text-[18px] font-black">{show(value(box))}<tspan className="fill-[color:var(--ink-3)] font-sans text-[11.5px] font-medium"> {unitWord(value, box)}</tspan></text>
              </>
            ) : (
              <>
                <text x={box.x + 12} y={box.y + 16} className={cn('text-[12px]', live ? 'fill-[color:var(--ink)]' : 'fill-[color:var(--ink-3)]')}>{box.label}</text>
                <text x={box.x + box.w - 10} y={box.y + 16} textAnchor="end" className="fill-[color:var(--ink-3)] text-[12px] font-semibold">{show(value(box))}</text>
              </>
            )}
          </g>
        );
      })}
      {stores.map((box) => {
        const on = activeLocation === box.location;
        return (
          <g key={box.location} {...activate(() => pick.store(box.location))} aria-label={`${box.location}: ${formatQty(box.in)} pcs in, ${formatQty(box.out)} pcs out`} aria-pressed={on} className="cursor-pointer outline-none [&:focus-visible>rect]:stroke-[color:var(--brand)]" data-testid={`stock-flow-store-${box.location.replace(/\s+/g, '-')}`}>
            {on ? <ActiveRing box={box} rx={box.busy ? 11 : 9} /> : null}
            <rect
              x={box.x + (on ? 1 : 0.5)}
              y={box.y + (on ? 1 : 0.5)}
              width={box.w - (on ? 2 : 1)}
              height={box.h - (on ? 2 : 1)}
              rx={box.busy ? 9 : 7}
              fill={box.busy ? 'var(--navy-600)' : on ? 'var(--brand-soft)' : 'var(--surface)'}
              stroke={on ? (box.busy ? '#fff' : 'var(--brand)') : box.busy ? 'var(--navy-600)' : 'var(--line)'}
              strokeWidth={on ? 2 : 1}
              data-active={on ? 'true' : undefined}
            />
            {!box.busy ? <circle cx={box.x + 11} cy={box.y + box.h / 2} r="3" fill={locationColor(box.location)} /> : null}
            <text x={box.x + (box.busy ? 12 : 21)} y={box.y + box.h / 2 + 4} className={cn(box.busy ? 'fill-white text-[12.5px] font-semibold' : 'fill-[color:var(--ink-2)] text-[12px]')}>{box.location}</text>
            <text x={box.x + box.w - 10} y={box.y + box.h / 2 + 4} textAnchor="end" className={cn('font-semibold tabular-nums', box.busy ? 'fill-white text-[12.5px] font-bold' : 'fill-[color:var(--ink-3)] text-[12px]')}>
              {show(readsPieces(value) ? box.in : box.inLines)}
            </text>
          </g>
        );
      })}
      {outs.map((box) => {
        const live = box.qty > 0;
        const on = activeType === box.filter && box.key !== 'written_off';
        return (
          <g key={box.key} {...activate(() => pick.out(box))} aria-label={`${box.label}: ${show(value(box))}`} aria-pressed={on} className="cursor-pointer outline-none [&:focus-visible>rect]:stroke-[color:var(--brand)]" data-testid={`stock-flow-out-${box.key}`}>
            {on ? <ActiveRing box={box} rx={10} /> : null}
            <rect x={box.x + 0.5} y={box.y + 0.5} width={box.w - 1} height={box.h - 1} rx="8" fill={live ? 'var(--status-violet-bg)' : 'var(--surface)'} stroke={on ? 'var(--brand)' : live ? 'var(--status-violet)' : 'var(--line)'} strokeWidth={on ? 2 : 1} />
            <text x={box.x + 12} y={box.y + 19} className={cn('text-[12.5px]', live ? 'fill-[color:var(--ink)] font-semibold' : 'fill-[color:var(--ink-2)]')}>{box.label}</text>
            <text x={box.x + box.w - 10} y={box.y + 19} textAnchor="end" className={cn('text-[12.5px] font-semibold tabular-nums', live ? 'fill-[color:var(--ink)]' : 'fill-[color:var(--ink-3)]')}>{show(value(box))}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** The box that filters the ledger: a brand ring 3px outside it, visible on the navy store box too. */
function ActiveRing({ box, rx }) {
  return <rect x={box.x - 3} y={box.y - 3} width={box.w + 6} height={box.h + 6} rx={rx} fill="none" stroke="var(--brand)" strokeWidth="2" aria-hidden="true" />;
}

/** True when the value function reads pieces (the store boxes switch between pieces and lines with it). */
function readsPieces(value) {
  return value({ qty: 1, lines: 0 }) === 1;
}

function unitWord(value, box) {
  return readsPieces(value) ? (box.qty === 1 ? 'pc' : 'pcs') : (box.lines === 1 ? 'line' : 'lines');
}

function FlowList({ flow, value, show, pick, activeLocation, activeType }) {
  const quiet = flow.stores.filter((entry) => !entry.in && !entry.out && activeLocation !== entry.location);
  const row = (key, label, number, onClick, active, tone, color) => (
    <li key={key}>
      <button
        type="button"
        data-testid={`stock-flow-row-${String(key).replace(/\s+/g, '-')}`}
        onClick={onClick}
        aria-pressed={active}
        className={cn(
          'flex min-h-[36px] w-full items-center gap-2 rounded-lg px-2.5 text-left text-[13.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          active ? 'bg-brand-soft font-semibold text-brand-ink' : 'hover:bg-surface-hover'
        )}
      >
        <span className={cn('h-2 w-2 shrink-0 rounded-full', tone)} style={color ? { background: color } : undefined} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <b className={cn('tabular-nums', number ? 'text-ink' : 'text-ink-4')}>{show(number)}</b>
      </button>
    </li>
  );
  return (
    <div className="grid gap-3" data-testid="stock-flow-list">
      <section>
        <UpperLabel className="mb-1 px-2.5">Sources</UpperLabel>
        <ul>{flow.sources.map((entry) => row(entry.key, entry.label, value(entry), () => pick.source(entry), activeType === entry.filter && entry.key !== 'found', 'bg-status-green'))}</ul>
      </section>
      <section>
        <UpperLabel className="mb-1 px-2.5">Stores · in</UpperLabel>
        <ul>{flow.stores.filter((entry) => entry.in || entry.out || activeLocation === entry.location).map((entry) => row(entry.location, entry.location, readsPieces(value) ? entry.in : entry.inLines, () => pick.store(entry.location), activeLocation === entry.location, '', locationColor(entry.location)))}</ul>
        {quiet.length ? <p className="px-2.5 pt-1 text-[12px] text-ink-3">{plural(quiet.length, 'store')} with no movement: {quiet.map((entry) => entry.location).join(' · ')}</p> : null}
      </section>
      <section>
        <UpperLabel className="mb-1 px-2.5">Out of stores</UpperLabel>
        <ul>{flow.outs.map((entry) => row(entry.key, entry.label, value(entry), () => pick.out(entry), activeType === entry.filter && entry.key !== 'written_off', 'bg-status-violet'))}</ul>
      </section>
      {flow.transfers.qty ? <p className="px-2.5 text-[12px] text-ink-3">{plural(flow.transfers.lines, 'transfer')} between stores · {formatQty(flow.transfers.qty)} pcs</p> : null}
    </div>
  );
}
